import { cx } from 'cva'
import Link from 'next/link'
import type { ComponentProps, FC } from 'react'
import type { ImageData, Maybe } from '../../frontend-types'
import { IconCircle } from './IconCircle'
import { ImageWithCorner } from './ImageWithCorner'
import { TextAreaContent } from './TextAreaContent'

export type TopicCardCTAType = {
	image?: ImageData
	title: Maybe<string>
	description: Maybe<string>
	href: string
}

export type TopicCardCTAProps = Omit<
	ComponentProps<typeof Link>,
	'className' | 'children' | 'title' | 'href'
> & {
	card: TopicCardCTAType
}

export const TopicCardCTA: FC<TopicCardCTAProps> = ({ card, ...props }) => {
	const { image, title, description, href } = card
	return (
		<Link
			{...props}
			href={href}
			target="_self"
			className="h-full flex flex-row gap-x-xl text-blue hover:text-blue-shade-40 active:text-blue-shade-70 tablet-l:flex-col group min-h-51"
		>
			<div className="flex-shrink-0 w-2/5 tablet-l:w-full h-full tablet-p:h-auto">
				<ImageWithCorner
					image={image}
					aspectRatio="aspect-card-topic"
					className="h-full"
					hover
					sizes="(max-width: 767px) 45vw, (max-width: 1023px) 25vw, (max-width: 1559px) 33.33vw, 456px"
				/>
			</div>
			<div className="relative flex flex-1 flex-col justify-between tablet-l:w-full tablet-l:pt-2xl tablet-l:pr-xl">
				<div className="flex flex-col gap-y-md">
					<span className="text-h3">{title}</span>
					<TextAreaContent className="text-h5" text={description} textSize="custom-default" />
				</div>
				<div className="mt-auto flex pt-xl pb-2xl tablet-p:self-end tablet-l:self-start">
					<IconCircle icon={['fas', 'chevron-right']} size="sm" />
				</div>
				<div
					className={cx(
						'absolute right-0 bottom-0 h-(length:--stroke-sm) bg-blue left-0 pointer-events-none transition-all ease-in-out duration-150 z-15',
						'group-hover:bg-blue-shade-40 group-active:bg-blue-shade-70 group-hover:h-(length:--stroke-md)',
					)}
					aria-hidden="true"
				/>
			</div>
		</Link>
	)
}
